import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { login } from '../services/auth';
import { setUserPassword, editUserStatus } from '../services/userservice';
import { validateUsername, validateInput, sanitizeInput } from '../utils/inputValidation';
import HondaLoader from '../components/common/HondaLoader';

// FLAG DE DESARROLLO - Cambiar a true para saltarse el login
const DEV_MODE = false; 

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Validando acceso...');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedDevUser, setSelectedDevUser] = useState('HONDA');
  
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pendingUserId, setPendingUserId] = useState(null);
  
  // Estados para mensajes de error de validación
  const [usernameError, setUsernameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  
  // Obtener intentos fallidos del localStorage
  const getFailedAttempts = () => {
    try {
      const attempts = localStorage.getItem('loginFailedAttempts');
      return attempts ? parseInt(attempts, 10) : 0;
    } catch {
      return 0;
    }
  };
  
  // Incrementar intentos fallidos
  const incrementFailedAttempts = () => {
    const currentAttempts = getFailedAttempts();
    const newAttempts = currentAttempts + 1;
    localStorage.setItem('loginFailedAttempts', newAttempts.toString());
    return newAttempts;
  };
  
  // Resetear intentos fallidos (al login exitoso)
  const resetFailedAttempts = () => {
    localStorage.removeItem('loginFailedAttempts');
  };
  
  // Bloquear usuario en el backend
  const blockUserAccount = async (username) => {
    try {
      await editUserStatus(username, 2); // 2 = BLOQUEADO
      return true;
    } catch (error) {
      console.error('Error al bloquear usuario:', error);
      return false;
    }
  };

  // Usuarios de desarrollo con módulos definidos
  const devUsers = {
    HONDA: {
      id: 999,
      username: 'juan.perez',
      name: 'Juan',
      lastname: 'Pérez',
      email: 'juan.perez@honda.com',
      group_ID: 2,
      groupname: 'HONDA',
      sessionId: 'dev-session-honda-999',
      modules: ['CESIONES', 'PRENDAS', 'SCORING', 'ABM'] // HONDA tiene acceso a todos los módulos
    },
    CONCESIONARIO: {
      id: 1000,
      username: 'carlos.lopez',
      name: 'Carlos',
      lastname: 'López',
      email: 'carlos.lopez@concesionario.com',
      group_ID: 1,
      groupname: 'CONCESIONARIO',
      sessionId: 'dev-session-concesionario-1000',
      modules: ['CESIONES', 'PRENDAS', 'SCORING'] // CONCESIONARIO no tiene ABM
    },
    XEROX: {
      id: 1001,
      username: 'maria.garcia',
      name: 'María',
      lastname: 'García',
      email: 'maria.garcia@xerox.com',
      group_ID: 3,
      groupname: 'XEROX',
      sessionId: 'dev-session-xerox-1001',
      modules: ['CESIONES', 'PRENDAS', 'SCORING', 'ABM'] // XEROX tiene acceso completo (aunque con isXerox() ve todo automáticamente)
    }
  };

  const handleDevLogin = () => {
    const devUser = devUsers[selectedDevUser];
    const userToStore = {
      id: devUser.id,
      username: devUser.username,
      name: devUser.name,
      lastname: devUser.lastname,
      email: devUser.email,
      group_ID: devUser.group_ID,
      groupname: devUser.groupname,
      sessionId: devUser.sessionId,
      modules: devUser.modules,
      status: 1,
      userResetPassword: 0
    };

    sessionStorage.setItem('userSession', JSON.stringify(userToStore));
    
    onLogin(userToStore);
    
    navigate('/inicio');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (DEV_MODE) {
      handleDevLogin();
      return;
    }
    
    if (!username || !password) {
      Swal.fire({
        icon: 'warning',
        title: 'Campos incompletos',
        text: 'Por favor, complete todos los campos',
        confirmButtonColor: '#CC0000'
      });
      return;
    }
    
    // Validar username
    const usernameValidation = validateUsername(username);
    if (!usernameValidation.isValid) {
      Swal.fire({
        icon: 'error',
        title: 'Usuario inválido',
        text: usernameValidation.message,
        confirmButtonColor: '#CC0000'
      });
      return;
    }
    
    // Validar password
    const passwordValidation = validateInput(password);
    if (!passwordValidation.isValid) {
      Swal.fire({
        icon: 'error',
        title: 'Contraseña inválida',
        text: passwordValidation.message,
        confirmButtonColor: '#CC0000'
      });
      return;
    }
    
    // Activar loading y registrar tiempo de inicio
    const startTime = Date.now();
    setLoading(true);
    setLoadingMessage('Validando acceso...');

    try {
      const data = await login(username, password);

      // ✅ Verificar validaciones del usuario usando el objeto validation
      if (data.validation) {
        // Usuario bloqueado (userStatus === 2)
        if (data.validation.isBlocked) {
          const elapsed = Date.now() - startTime;
          const remainingTime = Math.max(0, 1000 - elapsed);
          await new Promise(resolve => setTimeout(resolve, remainingTime));
          
          setLoading(false);
          navigate('/usuario-bloqueado');
          return;
        }
        
        // Usuario inactivo (userStatus === 0 pero ID existe) - Caso específico
        if (data.validation.isInactive) {
          const elapsed = Date.now() - startTime;
          const remainingTime = Math.max(0, 1000 - elapsed);
          await new Promise(resolve => setTimeout(resolve, remainingTime));
          
          Swal.fire({
            icon: 'warning',
            title: 'Cuenta desactivada',
            html: `
              <div style="text-align: center; padding: 15px;">
                <p style="margin-bottom: 15px; font-size: 16px; font-weight: 500;">
                  Su cuenta ha sido <strong style="color: #CC0000;">desactivada</strong> por un administrador.
                </p>
                <p style="margin-bottom: 10px; font-size: 15px; color: #555;">
                  No puede acceder al sistema en este momento.
                </p>
                <hr style="margin: 15px 0; border: none; border-top: 1px solid #ddd;">
                <p style="font-size: 14px; color: #666;">
                  Si cree que esto es un error, contacte al administrador del sistema.
                </p>
              </div>
            `,
            confirmButtonText: 'Entendido',
            confirmButtonColor: '#CC0000',
            width: '500px'
          });
          setLoading(false);
          return;
        }

        // Usuario debe cambiar contraseña obligatoriamente (passwordResetFlag === 1)
        if (data.validation.mustResetPassword) {
          const elapsed = Date.now() - startTime;
          const remainingTime = Math.max(0, 1000 - elapsed);
          await new Promise(resolve => setTimeout(resolve, remainingTime));
          
          // Mostrar mensaje informativo
          Swal.fire({
            icon: 'info',
            title: 'Cambio de contraseña obligatorio',
            html: `
              <div style="text-align: center; padding: 15px;">
                <p style="margin-bottom: 15px; font-size: 16px; font-weight: 500;">
                  Debe cambiar su contraseña antes de continuar.
                </p>
                <p style="margin-bottom: 10px; font-size: 15px; color: #555;">
                  Por seguridad, es necesario que establezca una nueva contraseña.
                </p>
                <hr style="margin: 15px 0; border: none; border-top: 1px solid #ddd;">
                <p style="font-size: 14px; color: #666;">
                  Contacte al administrador del sistema para cambiar su contraseña.
                </p>
              </div>
            `,
            confirmButtonText: 'Continuar',
            confirmButtonColor: '#CC0000',
            width: '500px',
            allowOutsideClick: false
          });
          
          setLoading(false);
          return;
        }

        // Usuario no encontrado o credenciales incorrectas
        if (data.validation.notFound || !data.id || data.id === 0) {
          const elapsed = Date.now() - startTime;
          const remainingTime = Math.max(0, 1000 - elapsed);
          await new Promise(resolve => setTimeout(resolve, remainingTime));
          
          // Incrementar contador de intentos fallidos
          const failedAttempts = incrementFailedAttempts();
          const remainingAttempts = 3 - failedAttempts;
          
          // Si alcanzó 3 intentos, bloquear la cuenta
          if (failedAttempts >= 3) {
            setLoadingMessage('Bloqueando cuenta por seguridad...');
            setLoading(true);
            
            // Intentar bloquear la cuenta en el backend
            const blocked = await blockUserAccount(username);
            
            setLoading(false);
            
            if (blocked) {
              // Resetear contador después del bloqueo
              resetFailedAttempts();
              
              Swal.fire({
                icon: 'error',
                title: 'Cuenta bloqueada',
                html: `
                  <div style="text-align: center; padding: 15px;">
                    <p style="margin-bottom: 15px; font-size: 16px; font-weight: 500; color: #CC0000;">
                      Su cuenta ha sido <strong>bloqueada</strong> por exceder el número de intentos permitidos.
                    </p>
                    <hr style="margin: 15px 0; border: none; border-top: 1px solid #ddd;">
                    <p style="font-size: 15px; color: #555; margin-bottom: 10px;">
                      Por razones de seguridad, su cuenta ha sido bloqueada automáticamente.
                    </p>
                    <p style="font-size: 14px; color: #666;">
                      Contacte al administrador del sistema para desbloquear su cuenta.
                    </p>
                  </div>
                `,
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#CC0000',
                width: '500px',
                allowOutsideClick: false
              }).then(() => {
                // Limpiar formulario
                setUsername('');
                setPassword('');
              });
            } else {
              // Si no se pudo bloquear en el backend, mostrar advertencia crítica
              Swal.fire({
                icon: 'error',
                title: 'Credenciales incorrectas',
                html: `
                  <div style="text-align: left; padding: 10px;">
                    <p style="margin-bottom: 15px; font-size: 15px;">
                      El usuario o la contraseña ingresados son incorrectos.
                    </p>
                    <div style="background-color: #fee; border-left: 4px solid #CC0000; padding: 12px; margin-top: 15px; border-radius: 4px;">
                      <p style="color: #CC0000; font-weight: bold; margin: 0 0 8px 0; font-size: 15px;">
                        ⚠️ ADVERTENCIA CRÍTICA
                      </p>
                      <p style="color: #666; margin: 0; font-size: 14px;">
                        Ha agotado sus intentos de inicio de sesión. Por favor, contacte al administrador.
                      </p>
                    </div>
                  </div>
                `,
                confirmButtonText: 'Entendido',
                confirmButtonColor: '#CC0000',
                width: '550px'
              });
            }
            setLoading(false);
            return;
          }
          
          let warningMessage = '';
          let iconType = 'warning';
          
          if (failedAttempts === 1) {
            // Primer intento fallido - Advertencia informativa (quedan 2 intentos)
            iconType = 'warning';
            warningMessage = `
              <div style="background-color: #fffbf0; border-left: 4px solid #fbbf24; padding: 14px; margin-top: 15px; border-radius: 6px;">
                <p style="color: #92400e; margin: 0; font-size: 14px; line-height: 1.5;">
                  <span style="font-size: 16px; margin-right: 6px;">⚠️</span>
                  <strong>Nota:</strong> Le quedan <strong>2 intentos</strong> antes de que su cuenta sea bloqueada por seguridad.
                </p>
              </div>
            `;
          } else if (failedAttempts === 2) {
            // Segundo intento fallido - Advertencia moderada (queda 1 intento)
            iconType = 'warning';
            warningMessage = `
              <div style="background-color: #fef3c7; border-left: 5px solid #f59e0b; padding: 14px; margin-top: 15px; border-radius: 6px; box-shadow: 0 2px 4px rgba(245, 158, 11, 0.1);">
                <p style="color: #92400e; font-weight: 600; margin: 0 0 6px 0; font-size: 15px; display: flex; align-items: center; gap: 6px;">
                  <span style="font-size: 18px;">⚠️</span>
                  PRECAUCIÓN
                </p>
                <p style="color: #78350f; margin: 0; font-size: 14px; line-height: 1.5;">
                  Le quedan <strong style="color: #f59e0b;">2 intentos</strong> antes de que su cuenta sea bloqueada por seguridad.
                </p>
              </div>
            `;
          }
          
          Swal.fire({
            icon: iconType,
            title: 'Credenciales incorrectas',
            html: `
              <div style="text-align: left; padding: 10px;">
                <p style="margin-bottom: 15px; font-size: 15px;">
                  El usuario o la contraseña ingresados son incorrectos.
                </p>
                <p style="font-size: 14px; color: #666; margin-bottom: 8px;">
                  <strong>Verifique que:</strong>
                </p>
                <ul style="text-align: left; color: #666; font-size: 14px; margin-left: 20px; margin-bottom: 15px;">
                  <li>El nombre de usuario esté escrito correctamente</li>
                  <li>La contraseña sea la correcta (distingue mayúsculas y minúsculas)</li>
                  <li>No haya espacios adicionales al inicio o final</li>
                </ul>
                ${warningMessage}
              </div>
            `,
            confirmButtonText: 'Entendido',
            confirmButtonColor: '#CC0000',
            width: '550px'
          });
          setLoading(false);
          return;
        }
      }

      if (data.passworD_BLANK === "true") {
        setPendingUserId(data.id);
        setShowPasswordModal(true);
        // Asegurar delay mínimo de 1 segundo
        const elapsed = Date.now() - startTime;
        const remainingTime = Math.max(0, 1000 - elapsed);
        await new Promise(resolve => setTimeout(resolve, remainingTime));
        setLoading(false);
        return;
      }

      // Normalizar módulos a array de strings
      let normalizedModules = [];
      if (Array.isArray(data.modules)) {
        normalizedModules = data.modules.map(module => {
          // Si es string, retornar tal cual
          if (typeof module === 'string') {
            return module;
          }
          // Si es objeto, intentar extraer el nombre
          if (typeof module === 'object' && module !== null) {
            return module.name || module.module || module.moduleName || 
                   module.NAME || module.MODULE || module.modulE_NAME || 'UNKNOWN';
          }
          return 'UNKNOWN';
        }).filter(m => m !== 'UNKNOWN'); // Filtrar módulos no reconocidos
      }
      
      // Construir sesión con módulos incluidos
      // IMPORTANTE: El sessionId viene del backend
      // NUNCA generar sessionId en el frontend
      const backendSessionId = data.sessionID || data.sessionId || null;
      
      if (!backendSessionId) {
        console.error('⚠️ El backend no retornó sessionId. Esto puede causar problemas de sesión concurrente.');
      }
      
      // Dividir nombre completo si viene junto (ej: "Abraham Verde")
      let firstName = data.name || username;
      // El API devuelve lastName con mayúscula
      let lastName = data.lastName || data.lastname || '';
      
      // Si no viene lastName pero name tiene espacios, separarlo
      if (!lastName && data.name && data.name.includes(' ')) {
        const nameParts = data.name.trim().split(' ');
        firstName = nameParts[0];
        lastName = nameParts.slice(1).join(' '); // Resto del nombre como apellido
      }
      
      // Obtener groupname: viene directamente del CheckLogin
      let groupname = data.groupname || data.groupName || null;
      
      // Usar el apellido que devuelve CheckLogin
      const finalLastName = lastName;
      
      const userToStore = {
        id: data.id,
        username: username,
        name: firstName,
        lastname: finalLastName,
        email: data.email || '',
        group_ID: data.groupID || null,
        groupname: groupname,
        sessionId: backendSessionId,
        modules: normalizedModules,
        status: data.userStatus ?? 1, 
        userResetPassword: data.passwordResetFlag ?? 0, 
        ce_ID: data.ceID || null, 
        creationDate: data.creationDate || null,
        lastLoginDate: data.lastLoginDate || null
      };

      sessionStorage.setItem('userSession', JSON.stringify(userToStore));
      
      setLoadingMessage('Iniciando sesión...');
      
      // Login exitoso - resetear contador de intentos fallidos
      resetFailedAttempts();
      
      onLogin(userToStore);
      
      // Asegurar delay mínimo de 1 segundo antes de redirigir
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, 1000 - elapsed);
      await new Promise(resolve => setTimeout(resolve, remainingTime));
      
      navigate('/inicio');

    } catch (err) {
      console.error('Error en login:', err);
      
      // Asegurar delay mínimo de 1 segundo
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, 1000 - elapsed);
      await new Promise(resolve => setTimeout(resolve, remainingTime));
      
      // Determinar si es error de conexión o error de credenciales
      const isConnectionError = err.type === 'connection' || 
                                err.type === 'server' ||
                                err.message.includes('Failed to fetch') || 
                                err.message.includes('NetworkError') ||
                                !navigator.onLine;
      
      if (isConnectionError) {
        // Error real de conexión - NO incrementar contador de intentos
        Swal.fire({
          icon: 'error',
          title: 'Error de conexión',
          html: `
            <div style="text-align: center; padding: 10px;">
              <p style="margin-bottom: 15px; font-size: 15px;">
                No se pudo conectar con el servidor.
              </p>
              <p style="font-size: 14px; color: #666;">
                Verifique su conexión a internet e intente nuevamente.
              </p>
            </div>
          `,
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#CC0000',
          width: '450px'
        });
      } else {
        // Error de autenticación (credenciales incorrectas) - Incrementar contador
        const failedAttempts = incrementFailedAttempts();
        
        // Si alcanzó 3 intentos, bloquear la cuenta
        if (failedAttempts >= 3) {
          setLoadingMessage('Bloqueando cuenta por seguridad...');
          setLoading(true);
          
          // Intentar bloquear la cuenta en el backend
          const blocked = await blockUserAccount(username);
          
          setLoading(false);
          
          if (blocked) {
            // Resetear contador después del bloqueo
            resetFailedAttempts();
            
            Swal.fire({
              icon: 'error',
              title: 'Cuenta bloqueada',
              html: `
                <div style="text-align: center; padding: 15px;">
                  <p style="margin-bottom: 15px; font-size: 16px; font-weight: 500; color: #CC0000;">
                    Su cuenta ha sido <strong>bloqueada</strong> por exceder el número de intentos permitidos.
                  </p>
                  <hr style="margin: 15px 0; border: none; border-top: 1px solid #ddd;">
                  <p style="font-size: 15px; color: #555; margin-bottom: 10px;">
                    Por razones de seguridad, su cuenta ha sido bloqueada automáticamente.
                  </p>
                  <p style="font-size: 14px; color: #666;">
                    Contacte al administrador del sistema para desbloquear su cuenta.
                  </p>
                </div>
              `,
              confirmButtonText: 'Entendido',
              confirmButtonColor: '#CC0000',
              width: '500px',
              allowOutsideClick: false
            }).then(() => {
              // Limpiar formulario
              setUsername('');
              setPassword('');
            });
          } else {
            // Si no se pudo bloquear en el backend, mostrar advertencia crítica
            Swal.fire({
              icon: 'error',
              title: 'Demasiados intentos fallidos',
              html: `
                <div style="text-align: center; padding: 15px;">
                  <p style="margin-bottom: 15px; font-size: 15px;">
                    Ha excedido el número máximo de intentos permitidos.
                  </p>
                  <p style="font-size: 14px; color: #666;">
                    Por favor, contacte al administrador del sistema.
                  </p>
                </div>
              `,
              confirmButtonText: 'Entendido',
              confirmButtonColor: '#CC0000',
              width: '450px'
            });
          }
          return;
        }
        
        let warningMessage = '';
        let iconType = 'warning';
        
        if (failedAttempts === 1) {
          // Primer intento fallido - Advertencia informativa (quedan 2 intentos)
          iconType = 'warning';
          warningMessage = `
            <div style="background-color: #fffbf0; border-left: 4px solid #fbbf24; padding: 14px; margin-top: 15px; border-radius: 6px;">
              <p style="color: #92400e; margin: 0; font-size: 14px; line-height: 1.5;">
                ⚠️ <strong>Nota:</strong> Le quedan <strong>2 intentos</strong> antes de que su cuenta sea bloqueada por seguridad.
              </p>
            </div>
          `;
        } else if (failedAttempts === 2) {
          // Segundo intento fallido - Advertencia urgente (queda 1 intento)
          iconType = 'warning';
          warningMessage = `
            <div style="background-color: #fee2e2; border-left: 5px solid #ef4444; padding: 14px; margin-top: 15px; border-radius: 6px; box-shadow: 0 2px 6px rgba(239, 68, 68, 0.15);">
              <p style="color: #7f1d1d; margin: 0; font-size: 14px; line-height: 1.5;">
               ⚠️<strong>Nota:</strong> Le queda <strong style="color: #dc2626; font-size: 15px;">1 intento</strong> antes de que su cuenta sea <strong>bloqueada automáticamente</strong>.
              </p>
            </div>
          `;
        }
        
        Swal.fire({
          icon: iconType,
          title: 'Credenciales incorrectas',
          html: `
            <div style="text-align: left; padding: 10px;">
              <p style="margin-bottom: 15px; font-size: 15px;">
                El usuario o la contraseña ingresados son incorrectos.
              </p>
              <p style="font-size: 14px; color: #666; margin-bottom: 8px;">
                <strong>Verifique que:</strong>
              </p>
              <ul style="text-align: left; color: #666; font-size: 14px; margin-left: 20px; margin-bottom: 15px;">
                <li>El nombre de usuario esté escrito correctamente</li>
                <li>La contraseña sea la correcta (distingue mayúsculas y minúsculas)</li>
              </ul>
              ${warningMessage}
            </div>
          `,
          confirmButtonText: 'Entendido',
          confirmButtonColor: '#CC0000',
          width: '550px'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSave = async () => {
    if (!newPassword || !confirmPassword) {
      Swal.fire({
        icon: 'warning',
        title: 'Campos incompletos',
        text: 'Complete ambos campos de contraseña',
        confirmButtonColor: '#CC0000'
      });
      return;
    }
    
    // Validar nueva contraseña
    const newPasswordValidation = validateInput(newPassword);
    if (!newPasswordValidation.isValid) {
      Swal.fire({
        icon: 'error',
        title: 'Contraseña inválida',
        text: newPasswordValidation.message,
        confirmButtonColor: '#CC0000'
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      Swal.fire({
        icon: 'error',
        title: 'Las contraseñas no coinciden',
        text: 'Por favor, verifique que ambas contraseñas sean iguales',
        confirmButtonColor: '#CC0000'
      });
      return;
    }

    if (newPassword.length < 6) {
      Swal.fire({
        icon: 'warning',
        title: 'Contraseña muy corta',
        text: 'La contraseña debe tener al menos 6 caracteres',
        confirmButtonColor: '#CC0000'
      });
      return;
    }

    try {
      await setUserPassword(pendingUserId, newPassword);
      
      Swal.fire({
        icon: 'success',
        title: '¡Contraseña creada!',
        text: 'Su contraseña ha sido establecida exitosamente. Por favor, inicie sesión nuevamente.',
        confirmButtonColor: '#CC0000'
      });
      
      setShowPasswordModal(false);
      setNewPassword('');
      setConfirmPassword('');
      setPendingUserId(null);
      setPassword('');
      
    } catch (err) {
      console.error('Error al establecer contraseña:', err);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo establecer la contraseña. Intente nuevamente.',
        confirmButtonColor: '#CC0000'
      });
    }
  };

  return (
    <>
      {/* Loader de moto Honda */}
      {loading && <HondaLoader message={loadingMessage} variant="overlay" size="medium" />}
      
      {/* Selector de usuario DEMO - Mejorado */}
      {DEV_MODE && (
        <div className="fixed top-6 right-6 z-50 glass border-2 border-gray-200 rounded-2xl shadow-xl p-3 flex gap-2 animate-fade-in">
          <button
            onClick={() => setSelectedDevUser('HONDA')}
            className={`px-4 py-2 text-sm font-bold rounded-xl transition-all duration-300 ${
              selectedDevUser === 'HONDA'
                ? 'gradient-red text-white shadow-glow-red'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md'
            }`}
          >
            HONDA
          </button>
          <button
            onClick={() => setSelectedDevUser('CONCESIONARIO')}
            className={`px-4 py-2 text-sm font-bold rounded-xl transition-all duration-300 ${
              selectedDevUser === 'CONCESIONARIO'
                ? 'gradient-red text-white shadow-glow-red'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md'
            }`}
          >
            CONCESIONARIO
          </button>
          <button
            onClick={() => setSelectedDevUser('XEROX')}
            className={`px-4 py-2 text-sm font-bold rounded-xl transition-all duration-300 ${
              selectedDevUser === 'XEROX'
                ? 'gradient-red text-white shadow-glow-red'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md'
            }`}
          >
            XEROX
          </button>
        </div>
      )}

      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center p-4">
        {/* Barra superior decorativa */}
        <div className="fixed top-0 left-0 right-0 h-2 gradient-red shadow-glow-red"></div>
        
        <div className="relative w-full max-w-md animate-fade-in-up">
          <div className="glass border-2 border-gray-200 rounded-3xl shadow-2xl overflow-hidden hover:shadow-glow-red transition-all duration-500">
            
            <div className="p-10">
              {/* Logo y título mejorados */}
              <div className="flex flex-col items-center mb-12">
                <div className="relative">
                  <div className="absolute inset-0 bg-honda-100 rounded-2xl blur-2xl opacity-50"></div>
                  <img 
                    src="/Hondalogo.png" 
                    alt="Honda Logo" 
                    className="relative h-24 w-auto object-contain drop-shadow-xl"
                  />
                </div>
                <p className="text-gray-600 text-sm font-medium">Plataforma Integral de Gestión Digital</p>
                <div className="flex items-center gap-2 mt-4">
                  <div className="w-8 h-0.5 bg-gradient-to-r from-transparent to-honda-400 rounded-full"></div>
                  <div className="w-12 h-1 gradient-red rounded-full shadow-glow-red"></div>
                  <div className="w-8 h-0.5 bg-gradient-to-l from-transparent to-honda-400 rounded-full"></div>
                </div>
              </div>
              
              {/* Formulario mejorado */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-3 uppercase tracking-wide flex items-center gap-2">
                    <span className="material-icons text-honda-600 text-lg">person</span>
                    Usuario
                  </label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      const sanitized = sanitizeInput(newValue);
                      
                      // No permitir espacios en el usuario
                      if (sanitized.includes(' ')) {
                        setUsernameError('El nombre de usuario no puede contener espacios');
                        return;
                      }
                      
                      const validation = validateUsername(sanitized);
                      if (!validation.isValid) {
                        setUsernameError(validation.message);
                      } else {
                        setUsernameError('');
                      }
                      
                      setUsername(sanitized);
                    }}
                    disabled={loading}
                    className={`w-full px-4 py-3.5 bg-white border-2 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none transition-all duration-300 shadow-inner-soft disabled:opacity-50 disabled:cursor-not-allowed ${
                      usernameError ? 'border-red-500 focus:border-red-500 focus:ring-4 focus:ring-red-100' : 'border-gray-200 focus:border-honda-500 focus:ring-4 focus:ring-honda-100'
                    }`}
                    placeholder="Ingrese su usuario"
                    autoComplete="username"
                  />
                  {usernameError && (
                    <p className="text-red-600 text-xs mt-2 flex items-center gap-1">
                      <span className="material-icons text-xs">error</span>
                      {usernameError}
                    </p>
                  )}
                </div>
                
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-3 uppercase tracking-wide flex items-center gap-2">
                    <span className="material-icons text-honda-600 text-lg">lock</span>
                    Contraseña
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        const sanitized = sanitizeInput(newValue);
                        
                        const validation = validateInput(sanitized);
                        if (!validation.isValid) {
                          setPasswordError(validation.message);
                        } else {
                          setPasswordError('');
                        }
                        
                        setPassword(sanitized);
                      }}
                      disabled={loading}
                      className={`w-full px-4 py-3.5 bg-white border-2 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none transition-all duration-300 shadow-inner-soft disabled:opacity-50 disabled:cursor-not-allowed ${
                        passwordError ? 'border-red-500 focus:border-red-500 focus:ring-4 focus:ring-red-100' : 'border-gray-200 focus:border-honda-500 focus:ring-4 focus:ring-honda-100'
                      }`}
                      placeholder="Ingrese su contraseña"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-honda-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="material-icons text-xl">
                        {showPassword ? 'visibility_off' : 'visibility'}
                      </span>
                    </button>
                  </div>
                  {passwordError && (
                    <p className="text-red-600 text-xs mt-2 flex items-center gap-1">
                      <span className="material-icons text-xs">error</span>
                      {passwordError}
                    </p>
                  )}
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 gradient-red hover:shadow-glow-red-lg text-white font-bold rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-soft uppercase tracking-wide ripple-effect hover:-translate-y-0.5"
                >
                  <span className="flex items-center justify-center gap-2">
                    <span>Ingresar</span>
                  </span>
                </button>
              </form>
              
            </div>
          </div>
          
          {/* Footer mejorado */}
          <div className="mt-8 flex flex-col items-center justify-center animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <div className="flex items-center justify-center gap-2 mb-3">
              <span className="text-gray-400 text-xs font-medium">powered by</span>
              <img 
                src="/logoDatco.png" 
                alt="Datco Logo" 
                className="h-6 w-auto object-contain opacity-60 hover:opacity-100 transition-opacity duration-300"
              />
            </div>
            <p className="text-center text-gray-400 text-xs flex items-center gap-2">
              <span className="material-icons text-xs">copyright</span>
              2026 Honda Motor Co., Ltd. - Todos los derechos reservados
            </p>
          </div>
        </div>
      </div>

      {/* Modal de contraseña mejorado */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="glass border-2 border-gray-200 rounded-2xl shadow-2xl max-w-md w-full animate-scale-in">
            <div className="p-8">
              <div className="text-center mb-8">
                <div className="relative inline-block mb-4">
                  <div className="absolute inset-0 bg-honda-100 rounded-full blur-xl opacity-50"></div>
                  <div className="relative w-20 h-20 bg-gradient-to-br from-honda-50 to-honda-100 rounded-full flex items-center justify-center mx-auto shadow-soft">
                    <span className="material-icons text-honda-600 text-4xl">lock_reset</span>
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-gradient mb-3">
                  Crear Contraseña
                </h2>
                <p className="text-gray-600 text-sm">
                  Su usuario no tiene contraseña asignada. Por favor, cree una nueva contraseña para continuar.
                </p>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2 flex items-center gap-2">
                    <span className="material-icons text-honda-600 text-lg">lock</span>
                    Nueva Contraseña
                  </label>
                  <input
                    type="password"
                    placeholder="Ingrese su nueva contraseña"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-honda-500 focus:ring-4 focus:ring-honda-100 transition-all duration-300"
                  />
                  <p className="text-gray-400 text-xs mt-2 flex items-center gap-1">
                    <span className="material-icons text-xs">info</span>
                    Mínimo 8 caracteres
                  </p>
                </div>
                
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2 flex items-center gap-2">
                    <span className="material-icons text-honda-600 text-lg">check_circle</span>
                    Confirmar Contraseña
                  </label>
                  <input
                    type="password"
                    placeholder="Confirme su nueva contraseña"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-honda-500 focus:ring-4 focus:ring-honda-100 transition-all duration-300"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-100">
                <button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setNewPassword('');
                    setConfirmPassword('');
                    setPendingUserId(null);
                  }}
                  className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-all duration-300 text-sm font-semibold hover:shadow-md"
                >
                  Cancelar
                </button>
                <button
                  onClick={handlePasswordSave}
                  className="px-5 py-2.5 gradient-red hover:shadow-glow-red text-white rounded-xl transition-all duration-300 text-sm font-semibold ripple-effect"
                >
                  Guardar Contraseña
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Login;
